import { React, useCallback, useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import "./index.scss";

import SideNav from "../../components/PostLogin/SideNav";
import ProfileAvatar from '../../components/ui/ProfileAvatar';
import SideModal from "../../components/ui/SideModal";
import { toaster } from "../../components/ui/Toaster";
import SkeletonLoader from "../../components/ui/SkeletonLoader";

import OrganizationTab from "../../components/PostLogin/UserDetails/OrganizationTab";
import ScanHistoryTab from "../../components/PostLogin/UserDetails/ScanHistory";
import UserSessionTab from "../../components/PostLogin/UserDetails/UserSessionTab";
import EditUserModal from "../../components/PostLogin/UserDetails/EditUserModal";
import DeleteUserModal from "../../components/PostLogin/UserDetails/DeleteUserModal";

import { editIcon, deleteIcon, headerArrowRightIcon, userDetailsRoleIcon } from "../../utils/icons";
import { getUserImageUrl,formatDate, formatNumber } from "../../utils/util";

import { useSelector, useDispatch } from 'react-redux';
import { getUserDetails, resetEditUserResponse, resetDeleteUserResponse } from '../../store/actions/usersAction';

const UserDetails = () => {

    const userDetailResponse = useSelector((state) => state.users.userDetails);
    const editUserResponse = useSelector((state) => state?.users?.editUserResponse);
    const deleteUserResponse = useSelector((state) => state?.users?.deleteUserResponse)

    const { t } = useTranslation();

    const { userId } = useParams();

    const dispatch = useDispatch();

    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState("organization");
    const [showEditUserModal, setShowEditUsermodal] = useState(false);
    const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const backToUsersPage = () => {
        navigate(`/users`)
    }

    const closeEditUserModal = () => {
        if(editUserResponse?.detail?.status === "success") {
            fetchUserDetails()
        }
        dispatch(resetEditUserResponse())
        setShowEditUsermodal(false)
    }

    const fetchUserDetails = useCallback (()=>{
        let obj = {
            user_id: userId
        }
        setIsLoading(true)
        dispatch(getUserDetails(obj))
    },[dispatch, userId])

    useEffect(()=>{
        fetchUserDetails()
    },[fetchUserDetails])

    useEffect(()=>{
        if(userDetailResponse != null) {
            setIsLoading(false)
        }
    },[userDetailResponse])

    useEffect(()=>{
        if(deleteUserResponse !== null) {
            setShowDeleteUserModal(false)
            if(deleteUserResponse?.detail?.status === "success") {
                toaster.addToast(deleteUserResponse?.detail?.description, "success");
                navigate('/users')
            } else {
                toaster.addToast(deleteUserResponse?.description, "error");
            }
            dispatch(resetDeleteUserResponse())
        }
    },[deleteUserResponse, navigate, dispatch])

    return (
        <div className="users-details-container">
            <SideNav />
            <div className="users-page-container">
                <div className="page-header-container px-4 py-3 d-flex align-items-center justify-content-between">
                    <div className="header-left d-flex align-items-center gap-3">
                        <h1 onClick={()=>{backToUsersPage()}}>{t("userDetailsPage.heading")}</h1>
                        {headerArrowRightIcon()}
                        <ProfileAvatar type="profile" firstName={userDetailResponse?.firstname} lastName={userDetailResponse?.lastname} imageUrl={userDetailResponse !== null ? getUserImageUrl(userDetailResponse) : ""} />
                    </div>
                    <div className="header-right d-flex gap-3">
                        <button onClick={()=>{setShowEditUsermodal(true)}} disabled={isLoading}>
                            <p className="d-inline-block mx-1">{editIcon()}</p>
                            {t("userDetailsPage.editBtn")}
                        </button>
                        <button onClick={()=>{setShowDeleteUserModal(true)}}>
                            <p className="d-inline-block mx-1">{deleteIcon()}</p>
                            {t("userDetailsPage.deleteBtn")}
                        </button>
                    </div>
                </div>
                <div className="page-main-section p-4">
                    <div className="organization-created d-flex justify-content-between">
                        <h4>{t("userDetailsPage.subHeading")}</h4>
                    </div>
                    <div className="user-header-container p-3 my-3">
                        {
                            isLoading ?
                            <SkeletonLoader type={"line"} count={3} currentPage={"userDetails"} />
                            :
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4 }}
                            >
                                <div className="d-flex justify-content-between">
                                    <div className="header-details d-flex gap-3">
                                        <p>{t("userDetailsPage.timeZone")} <span>{userDetailResponse?.timezone}</span></p>
                                        <p>{t("userDetailsPage.joinedOn")} <span>{formatDate(userDetailResponse?.created_at)}</span></p>
                                        {userDetailResponse?.last_login !== null && <p>{t("userDetailsPage.lastLogin")} <span> {formatDate(userDetailResponse?.last_login)}</span></p>}
                                        <p>{t("userDetailsPage.2fa")} <span>{userDetailResponse?.two_factor_enabled ? "Enabled" : "Disabled"}</span></p>                                    
                                    </div>
                                    <div className="user-role-status d-flex gap-3">
                                        <p className="d-flex align-items-center"><span className="d-inline-block mx-1">{userDetailsRoleIcon()}</span>{t("userDetailsPage.role")} {userDetailResponse?.role}</p>
                                    </div>
                                </div>
                                <div className="d-flex gap-3 mt-3">
                                    <div className="total-domains-container rounded p-3">
                                        <div className='domain-icon'>
                                            <h4 className="m-0 mt-1">{formatNumber(userDetailResponse?.stats?.scans?.total)}</h4>
                                            <div className="user-count mx-3 mt-2">
                                                <span className='active-icon d-inline-block'></span>
                                                <span className='passive-icon d-inline-block'></span>
                                                <p className='total-scans-text'>{t("userDetailsPage.totalScan")}</p>
                                            </div>
                                        </div>
                                        <div className="p-wrapper d-flex justify-content-between gap-3">
                                            <p className='mt-2'><span className='count d-inline-block'>{formatNumber(userDetailResponse?.stats?.scans?.active)}</span>{t("domainPage.activeScans")}</p>
                                            <p className='mt-2'><span className='count-passive'>{formatNumber(userDetailResponse?.stats?.scans?.passive)}</span>{t("domainPage.passiveScans")}</p>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        }                        
                    </div>
                    <div className="organization-main-container p-3">
                        <div className="orgs-tab-container d-flex rounded">
                            <p className={`rounded text-center ${activeTab === "organization" ? "active" : ""}`} onClick={()=>setActiveTab("organization")}>{t("userDetailsPage.organizationTab")}</p>
                            <p className={`rounded text-center ${activeTab === "scanHistory" ? "active" : ""}`} onClick={()=>setActiveTab("scanHistory")}>{t("userDetailsPage.scanHistoryTab")}</p>
                            <p className={`rounded text-center ${activeTab === "userSession" ? "active" : ""}`} onClick={()=>setActiveTab("userSession")}>{t("userDetailsPage.userSessionTab")}</p>
                        </div>
                        {activeTab === "organization" && <OrganizationTab userDetails={userDetailResponse} currentPageType={"usersDetails"} />}
                        {activeTab === "scanHistory" && <ScanHistoryTab scanHistoryData={userDetailResponse} currentPageType={"usersDetails"} />}
                        {activeTab === "userSession" && <UserSessionTab />}
                    </div>
                </div>          
                <SideModal showSidePanel={showEditUserModal} hideSidePanel={closeEditUserModal}>
                    {showEditUserModal && <EditUserModal userDetails={userDetailResponse} closeEditUserModal={closeEditUserModal}/>}
                </SideModal>
                <DeleteUserModal show={showDeleteUserModal} userDetails={userDetailResponse} onHide={() => setShowDeleteUserModal(false)}/>
            </div>
        </div>
    )
}

export default UserDetails